import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from '../Footer/Footer';
import Agendamento from '../Agendamento/Agendamento';
import GerenciarPacientes from '../Pacientes/GerenciarPacientes';
import ConsultarAnamnese from '../ConsultarAvaliacoes/ConsultarAnamnese';
import ConsultarAvaliacaoFisica from '../ConsultarAvaliacoes/ConsultarAvaliacaoFisica';

import Consultas from '../Consultas/Consultas';
import { apiService } from '../../services/api';
import './DashboardNutricionista.css';

interface User {
  role: 'paciente' | 'nutricionista';
  name: string;
  email: string;
  avatar?: string;
}

interface DashboardNutricionistaProps {
  user: User;
  onLogout: () => void;
}

const DashboardNutricionista: React.FC<DashboardNutricionistaProps> = ({ user, onLogout }) => {
  const [currentPage, setCurrentPage] = useState('inicio');
  const [totalPacientes, setTotalPacientes] = useState(0);
  const [consultasAprovadas, setConsultasAprovadas] = useState(0);
  const [consultasPendentes, setConsultasPendentes] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [pacientes, consultas] = await Promise.all([
        apiService.getPacientes(),
        apiService.getConsultas()
      ]);
      
      setTotalPacientes(pacientes.length);
      
      const aprovadas = consultas.filter((c: any) => c.status === 'aprovada').length;
      const pendentes = consultas.filter((c: any) => 
        c.status === 'pendente' || 
        c.status === 'agendada' || 
        !c.status
      ).length;
      
      setConsultasAprovadas(aprovadas);
      setConsultasPendentes(pendentes);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setTotalPacientes(0);
      setConsultasAprovadas(0);
      setConsultasPendentes(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (currentPage === 'inicio') {
      carregarDados();
    } else {
      // For other pages, trigger a page refresh by re-setting the current page
      const page = currentPage;
      setCurrentPage('');
      setTimeout(() => setCurrentPage(page), 10);
    }
  };

  const renderContent = () => {
    switch (currentPage) {
      case 'pacientes':
        return <GerenciarPacientes onPacienteChange={carregarDados} />;
      case 'consultas':
        return <Consultas />;
      case 'agendamento':
        return <Agendamento user={user} />;

      case 'anamnese':
        return <ConsultarAnamnese />;
      case 'avaliacao-fisica':
        return <ConsultarAvaliacaoFisica />;
      default:
        return (
          <div className="dashboard-content">
            <div className="welcome-banner">
              <h2>Olá, {user.name}! 👩‍⚕️</h2>
              <p>Bem-vinda! Aqui você pode gerenciar consultas, pacientes e acompanhar o progresso dos seus atendimentos.</p>
            </div>
            
            <div className="dashboard-cards">
              <div className="dashboard-card">
                <h3>Total de Pacientes</h3>
                <div className="card-date">
                  {loading ? (
                    <output className="spinner-border spinner-border-sm">
                      <span className="visually-hidden">Carregando...</span>
                    </output>
                  ) : (
                    totalPacientes
                  )}
                </div>
                <small>Pacientes cadastrados</small>
              </div>
              
              <div className="dashboard-card">
                <h3>Consultas Aprovadas</h3>
                <div className="card-date">
                  {loading ? (
                    <output className="spinner-border spinner-border-sm">
                      <span className="visually-hidden">Carregando...</span>
                    </output>
                  ) : (
                    consultasAprovadas
                  )}
                </div>
                <small>Consultas confirmadas</small>
              </div>
              
              <div className="dashboard-card">
                <h3>Consultas Pendentes</h3>
                <div className="card-date">
                  {loading ? (
                    <output className="spinner-border spinner-border-sm">
                      <span className="visually-hidden">Carregando...</span>
                    </output>
                  ) : (
                    consultasPendentes
                  )}
                </div>
                <small>Aguardando aprovação</small>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="dashboard">
      <Sidebar currentPage={currentPage} onPageChange={setCurrentPage} user={user} />
      <div className="dashboard-main">
        <Header currentPage={currentPage} user={user} onLogout={onLogout} onRefresh={handleRefresh} />
        <div className="dashboard-content-wrapper">
          {renderContent()}
          <div className="d-flex justify-content-center my-4">
            <button 
              className="btn btn-outline-success rounded-circle p-3"
              onClick={handleRefresh}
              title="Atualizar"
            >
              <RefreshCw size={20} />
            </button>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default DashboardNutricionista;